import React, { useMemo } from 'react';
import * as THREE from 'three';
import { RolledSteelSection } from '../../types/beamSection';

/**
 * Rolled Steel I-Beam Section Component
 * Renders an engineering-accurate I-Beam cross-section along the beam
 * 
 * IMPORTANT: Matches BoxGeometry and CylinderGeometry behavior:
 * - Cross-section is in XZ plane
 * - Extrusion is along Y-axis
 * - Parent group applies quaternion rotation to align with beam direction
 * 
 * This ensures proper rendering for horizontal, vertical, and diagonal beams.
 */
interface RolledSteelSectionProps {
  section: RolledSteelSection;
  color: string;
}

export const RolledSteelSectionMesh: React.FC<RolledSteelSectionProps> = ({ section, color }) => {
  const { h, b, tf, tw, length } = section;
  
  // Convert mm to meters for Three.js (assuming beam units are in meters)
  const scale = 0.02; // 1mm = 0.001m
  
  const height = h * scale;
  const width = b * scale;
  const flangeThickness = tf * scale;
  const webThickness = tw * scale;
  
  /**
   * Create I-Beam cross-section shape
   * 
   * IMPORTANT: THREE.Shape draws in XY plane, ExtrudeGeometry extrudes along Z
   * But CylinderGeometry has its axis along Y
   * 
   * Solution:
   * 1. Draw I-beam shape in XY plane (X = width, Y = height)
   * 2. Extrude along Z for beam length
   * 3. Rotate 90° to make Z-axis align with Y-axis
   * 4. Result: Extrusion is now along Y-axis (matches CylinderGeometry)
   */
  const iBeamGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    
    // Draw I-beam cross-section in XY plane
    // X-axis = width (left-right)
    // Y-axis = height (up-down)
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const halfWeb = webThickness / 2;
    
    // Draw I-beam profile (clockwise from bottom-left)
    shape.moveTo(-halfWidth, -halfHeight);
    
    // Bottom flange
    shape.lineTo(halfWidth, -halfHeight);  // Bottom edge
    shape.lineTo(halfWidth, -halfHeight + flangeThickness);  // Right edge up
    
    // Right side of web
    shape.lineTo(halfWeb, -halfHeight + flangeThickness);
    shape.lineTo(halfWeb, halfHeight - flangeThickness);
    
    // Top flange (right side)
    shape.lineTo(halfWidth, halfHeight - flangeThickness);
    shape.lineTo(halfWidth, halfHeight);  // Right edge
    
    // Top flange (top edge)
    shape.lineTo(-halfWidth, halfHeight);
    
    // Top flange (left side)
    shape.lineTo(-halfWidth, halfHeight - flangeThickness);
    
    // Left side of web
    shape.lineTo(-halfWeb, halfHeight - flangeThickness);
    shape.lineTo(-halfWeb, -halfHeight + flangeThickness);
    
    // Back to start (left side of bottom flange)
    shape.lineTo(-halfWidth, -halfHeight + flangeThickness);
    shape.lineTo(-halfWidth, -halfHeight);
    
    // Extrude along Z-axis (default for ExtrudeGeometry)
    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: length,  // Extrudes along Z from 0 to +length
      bevelEnabled: false,
      steps: 1,
    };
    
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    
    // Now geometry is:
    // - Cross-section in XY plane
    // - Extruded along Z from 0 to +length
    
    // We need to:
    // 1. Rotate so extrusion is along Y-axis (like CylinderGeometry)
    // 2. Center along the extrusion axis
    
    // Rotate -90° around X-axis: converts Z-extrusion to Y-extrusion
    geometry.rotateX(-Math.PI / 2);
    
    // After rotation:
    // - Cross-section is now in XZ plane
    // - Extrusion is now along Y-axis from 0 to +length
    
    // Center on Y-axis (same as CylinderGeometry)
    geometry.translate(0, -length / 2, 0);
    
    // Final result:
    // - Cross-section in XZ plane
    // - Extruded along Y from -length/2 to +length/2
    // - Matches CylinderGeometry behavior exactly!
    
    return geometry;
  }, [height, width, flangeThickness, webThickness, length]);

  // Debug logging
  console.log('🔧 RolledSteelSectionMesh:', {
    designation: section.designation,
    length,
    dimensions: `${h}mm × ${b}mm (h × b)`,
    'scaled': `${height.toFixed(3)}m × ${width.toFixed(3)}m`,
    'extrusion axis': 'Y-axis (after rotation)',
    'geometry span': `Y: -${(length/2).toFixed(2)} to +${(length/2).toFixed(2)}`
  });

  return (
    <mesh geometry={iBeamGeometry}>
      <meshStandardMaterial
        color={color}
        transparent
        opacity={0.4}
        side={THREE.DoubleSide}
        metalness={0.6}
        roughness={0.4}
      />
      {/* Wireframe overlay for better visualization */}
      <lineSegments>
        <edgesGeometry args={[iBeamGeometry]} />
        <lineBasicMaterial color={color} linewidth={2} />
      </lineSegments>
    </mesh>
  );
};
